/* @flow */
import * as React from 'react';
import { observable } from 'mobx';
import { NavLink, Link, withRouter } from 'react-router-dom';

import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';

import { observer, inject } from 'mobx-react';
import { translate } from 'react-i18next';

import { TERMS_OF_BUSINESS_URL, TERMS_OF_USE_URL, PRIVACY_POLICY_URL, EULA_URL } from 'config';

import { inlineManualService } from 'services/ServiceRegistry';

import AuthenticationStore from 'stores/AuthenticationStore';
import NavigationButton from 'components/NavigationButton/NavigationButton';
import Multilingual from 'components/Multilingual/Multilingual';
import ElectrorentLogo from 'components/ElectrorentLogo/ElectrorentLogo';
import { ElectroTooltip } from 'components/ExtendedUiElements/customized';
import { Roles } from 'permissions/defs';
import { rushAssetRequestFormPermissions } from 'permissions/rush';

import NotificationButton from './NotificationButton/NotificationButton';

import styles from './TopMenu.module.scss';
import NavigationService from 'services/NavigationService';

/** @see help.inlinemanual.com/docs/how-link-widget-button */
export function showInlineManualPanel() {
  if (window.inline_manual_player) {
    window.inline_manual_player.showPanel();
  }
}

function InlineManual({ t }): React.Element<*> {
  return (
    <div className={styles['inline-manual']}>
      <ElectroTooltip
        title={t('general:labels.click_guide', { title: process.env.REACT_APP_PORTAL_NAME })}
      >
        <NavigationButton onClick={showInlineManualPanel}>{t('general:help')}</NavigationButton>
      </ElectroTooltip>
    </div>
  );
}

@translate()
@withRouter
@inject('authenticationStore', 'userStore', 'actionDrawerStore', 'dashboardStore')
@observer
export default class TopMenu extends React.Component<*> {
  @observable localState: Object = {
    searchOpen: false,
    anchorEl: null,
  };

  handleClick = (e: SyntheticInputEvent<EventTarget>) => {
    this.localState.anchorEl = e.currentTarget;
  };

  handleClose = () => {
    this.localState.anchorEl = null;
  };

  logoutUser = (e: SyntheticInputEvent<EventTarget>) => {
    e.preventDefault();
    this.handleClose();
    inlineManualService.disableUpdatesHandler();
    this.props.actionDrawerStore.clearActionDrawer();
    this.props.authenticationStore.logout();
    this.props.userStore.clearUser();
    this.props.dashboardStore.removeDashboardFromLocal();
    this.props.history.push('/login');
  };

  renderLoggedInNav() {
    const t = this.props.t;
    const anchorEl = this.localState.anchorEl;
    const me = this.props.userStore.activeUser || {};
    const fullName = me.first_name || me.last_name ? `${me.first_name} ${me.last_name}` : '';
    const isSystemAdmin = me.role_id === Roles.SYSTEM_ADMIN;

    return (
      <>
        <span className={styles['account-username']} onClick={this.handleClick}>
          <NavigationButton>
            <svg>
              <use xlinkHref="#icon-profile" />
            </svg>
            <span className={styles['user-email']} data-hj-suppress>
              <strong>{process.env.REACT_APP_PORTAL_NAME}</strong>
              <strong>{!this.props.userStore.activeUser ? 'Loading...' : fullName}</strong>
            </span>
            <svg className={styles['account-options']}>
              <use xlinkHref="#icon-chevron-up" />
            </svg>
          </NavigationButton>
        </span>
        <Menu
          id="long-menu"
          className={styles['account-menu-options']}
          anchorEl={anchorEl}
          elevation={2}
          open={Boolean(anchorEl)}
          onClose={this.handleClose}
          getContentAnchorEl={null}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        >
          {me.is_enabled && (
            <MenuItem className={styles['account-menu-item']}>
              <Link
                to={NavigationService.formulateReportViewUri('/assets', {
                  page: 1,
                  filters: [{ key: 'user', values: [me.id] }],
                })}
                onClick={this.handleClose}
              >
                <svg>
                  <use xlinkHref="#icon-assets" />
                </svg>
                <span>{t('general:assets.my_assets')}</span>
              </Link>
            </MenuItem>
          )}

          {(me.asset_request_enabled || isSystemAdmin) && (
            <MenuItem className={styles['account-menu-item']}>
              <Link to="/asset-request" onClick={this.handleClose}>
                <svg>
                  <use xlinkHref="#icon-terms-conditions" />
                </svg>
                <span>{t('general:asset_request_form')}</span>
              </Link>
            </MenuItem>
          )}

          {(me.rush_asset_request_enabled || isSystemAdmin) && (
            <MenuItem className={styles['account-menu-item']}>
              <Link to="/asset-request-rush" onClick={this.handleClose}>
                <svg>
                  <use xlinkHref="#icon-terms-conditions" />
                </svg>
                <span>{t('general:rush_asset_request_form')}</span>
              </Link>
            </MenuItem>
          )}

          {(me.license_request_enabled || isSystemAdmin) && (
            <MenuItem className={styles['account-menu-item']}>
              <Link to="/license-request" onClick={this.handleClose}>
                <svg>
                  <use xlinkHref="#icon-terms-conditions" />
                </svg>
                <span>{t('general:license_request_form')}</span>
              </Link>
            </MenuItem>
          )}

          {me.is_enabled && (
            <MenuItem className={styles['account-menu-item']}>
              <Link to="/profile" onClick={this.handleClose}>
                <svg>
                  <use xlinkHref="#icon-manage" />
                </svg>
                <span>{t('general:profile_preferences')}</span>
              </Link>
            </MenuItem>
          )}
          <MenuItem className={styles['account-menu-item']}>
            <a
              href={TERMS_OF_USE_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={this.handleClose}
            >
              <svg>
                <use xlinkHref="#icon-terms-conditions" />
              </svg>
              <span>{t('general:terms_of_use')}</span>
            </a>
          </MenuItem>
          <MenuItem className={styles['account-menu-item']}>
            <a
              href={TERMS_OF_BUSINESS_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={this.handleClose}
            >
              <svg>
                <use xlinkHref="#icon-terms-conditions" />
              </svg>
              <span>{t('general:terms_of_business')}</span>
            </a>
          </MenuItem>
          <MenuItem className={styles['account-menu-item']}>
            <a
              href={PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={this.handleClose}
            >
              <svg>
                <use xlinkHref="#icon-terms-conditions" />
              </svg>
              <span>{t('general:cookie_privacy_policy')}</span>
            </a>
          </MenuItem>
          <MenuItem className={styles['account-menu-item']}>
            <a href={EULA_URL} target="_blank" rel="noopener noreferrer" onClick={this.handleClose}>
              <svg>
                <use xlinkHref="#icon-terms-conditions" />
              </svg>
              <span>{t('general:end_user_agreement')}</span>
            </a>
          </MenuItem>
          <MenuItem className={styles['account-menu-item']}>
            <Link to="/login" onClick={this.logoutUser}>
              <svg>
                <use xlinkHref="#icon-logout" />
              </svg>
              <span>{t('general:accounts.log_out')}</span>
            </Link>
          </MenuItem>
        </Menu>
        <NotificationButton />
      </>
    );
  }

  renderLoggedOutNav(): React.Element<*> {
    const t: Function = this.props.t;
    return (
      <span className={styles['account-username']}>
        <NavLink to="/login">
          <NavigationButton>
            <svg>
              <use xlinkHref="#icon-profile" />
            </svg>
            <span className={styles['user-email']}>{t('menu:Account Login')}</span>
          </NavigationButton>
        </NavLink>
      </span>
    );
  }

  render(): React.Element<*> {
    const authenticationStore: AuthenticationStore = this.props.authenticationStore;
    const t: Function = this.props.t;
    const isUserLoggedIn: string =
      authenticationStore.authenticationState === 'authed' ? '' : 'logged-out';
    const searchOpen: string = this.localState.searchOpen ? 'searchOpen' : '';

    return (
      <nav
        className={`${styles['topnav-container']} ${styles[isUserLoggedIn]} ${styles[searchOpen]}`}
      >
        <ElectrorentLogo />
        <div className={styles['topnav-wrapper']}>
          <div className={styles['topnav-links']}>
            <a
              href={`${process.env.REACT_APP_MARKETING_URL}/product-groups`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('general:product_plural')}
            </a>
            <a
              href={`${process.env.REACT_APP_MARKETING_URL}/manufacturers`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('general:manufacturer_plural')}
            </a>
            <a
              href={`${process.env.REACT_APP_MARKETING_URL}/services`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('general:service_plural')}
            </a>

            <a
              href={`${process.env.REACT_APP_MARKETING_URL}/solutions`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('general:solution_plural')}
            </a>

            <a
              href={`${process.env.REACT_APP_MARKETING_URL}/resources`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('general:resource_plural')}
            </a>
            <a
              href={`${process.env.REACT_APP_MARKETING_URL}/about-us`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('general:about_us')}
            </a>
            <a
              href={`${process.env.REACT_APP_MARKETING_URL}/contact-us`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('general:contact_us')}
            </a>
          </div>
          <div className={styles['topnav-account-info']}>
            <div className={styles['topnav-account-global-links']}>
              <InlineManual t={t} />
              <Multilingual />
            </div>
            <div className={styles['topnav-account-notification-panel']}>
              {authenticationStore.authenticationState === 'authed'
                ? this.renderLoggedInNav()
                : this.renderLoggedOutNav()}
            </div>
          </div>
        </div>
      </nav>
    );
  }
}
